(() => {
  const fallbackPositions = [
    { x: 0.06, y: 0.12 },
    { x: 0.72, y: 0.16 },
    { x: 0.1, y: 0.78 },
    { x: 0.7, y: 0.76 },
  ];
  const maskScale = 2;
  const activeHubs = new WeakMap();

  function readPixelMask(canvas) {
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return { width: 1, height: 1, data: new Uint8Array(1) };

    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const data = new Uint8Array(canvas.width * canvas.height);
    for (let pixel = 0; pixel < data.length; pixel += 1) {
      data[pixel] = pixels[pixel * 4 + 3] > 4 ? 1 : 0;
    }
    return { width: canvas.width, height: canvas.height, data };
  }

  function dilatePixelMask(mask, radius) {
    const data = new Uint8Array(mask.data.length);
    const offsets = [];

    for (let y = -radius; y <= radius; y += 1) {
      for (let x = -radius; x <= radius; x += 1) {
        if (x * x + y * y <= radius * radius) offsets.push({ x, y });
      }
    }

    for (let y = 0; y < mask.height; y += 1) {
      for (let x = 0; x < mask.width; x += 1) {
        if (!mask.data[y * mask.width + x]) continue;

        offsets.forEach((offset) => {
          const targetX = x + offset.x;
          const targetY = y + offset.y;
          if (
            targetX < 0 ||
            targetX >= mask.width ||
            targetY < 0 ||
            targetY >= mask.height
          ) {
            return;
          }
          data[targetY * mask.width + targetX] = 1;
        });
      }
    }

    return { ...mask, data };
  }

  function opaquePixelBounds(mask) {
    let left = mask.width;
    let top = mask.height;
    let right = 0;
    let bottom = 0;

    for (let y = 0; y < mask.height; y += 1) {
      for (let x = 0; x < mask.width; x += 1) {
        if (!mask.data[y * mask.width + x]) continue;
        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x + 1);
        bottom = Math.max(bottom, y + 1);
      }
    }

    return right > left && bottom > top
      ? { left, top, right, bottom }
      : { left: 0, top: 0, right: mask.width, bottom: mask.height };
  }

  function masksOverlap(first, second) {
    const left = Math.max(first.x, second.x);
    const top = Math.max(first.y, second.y);
    const right = Math.min(first.x + first.width, second.x + second.width);
    const bottom = Math.min(first.y + first.height, second.y + second.height);
    if (
      left >= right ||
      top >= bottom ||
      first.width <= 0 ||
      second.width <= 0
    ) {
      return false;
    }

    for (let worldY = Math.floor(top); worldY < Math.ceil(bottom); worldY += 1) {
      const sampleY = worldY + 0.5;
      const firstY = Math.floor(
        ((sampleY - first.y) / first.height) * first.mask.height,
      );
      const secondY = Math.floor(
        ((sampleY - second.y) / second.height) * second.mask.height,
      );
      if (
        firstY < 0 ||
        firstY >= first.mask.height ||
        secondY < 0 ||
        secondY >= second.mask.height
      ) {
        continue;
      }

      for (let worldX = Math.floor(left); worldX < Math.ceil(right); worldX += 1) {
        const sampleX = worldX + 0.5;
        const firstX = Math.floor(
          ((sampleX - first.x) / first.width) * first.mask.width,
        );
        const secondX = Math.floor(
          ((sampleX - second.x) / second.width) * second.mask.width,
        );
        if (
          firstX < 0 ||
          firstX >= first.mask.width ||
          secondX < 0 ||
          secondX >= second.mask.width
        ) {
          continue;
        }

        if (
          first.mask.data[firstY * first.mask.width + firstX] &&
          second.mask.data[secondY * second.mask.width + secondX]
        ) {
          return true;
        }
      }
    }

    return false;
  }

  function initializeHub(boundary) {
    if (activeHubs.has(boundary)) return;

    const nodes = Array.from(
      boundary.querySelectorAll(".floating-item[data-frml-label]"),
    );
    if (nodes.length !== fallbackPositions.length) return;

    let width = 0;
    let height = 0;
    let frame = 0;
    let previousTime = 0;
    let lastFrameAt = 0;
    let animationWatchdog = 0;
    let initialized = false;
    let ready = false;
    let disposed = false;
    let pressedItem = null;
    let pointer = { x: 0, y: 0, active: false };

    const items = nodes.map((node) => ({
      node,
      label: node.dataset.frmlLabel || node.textContent.trim(),
      x: 0,
      y: 0,
      width: 1,
      height: 1,
      mask: { width: 1, height: 1, data: new Uint8Array(1) },
      opaqueBounds: { left: 0, top: 0, right: 1, bottom: 1 },
      renderBounds: { left: 0, top: 0, right: 1, bottom: 1 },
      vx: 0,
      vy: 0,
      focused: false,
      pointerHeld: false,
      held: false,
      lastObservedX: 0,
      lastObservedY: 0,
      lastMovedAt: performance.now(),
    }));

    const bodyForItem = (item, x = item.x, y = item.y) => ({
      x,
      y,
      width: item.width,
      height: item.height,
      mask: item.mask,
    });

    const keepMoving = (item) => {
      const speed = Math.hypot(item.vx, item.vy);
      if (speed < 1) {
        const angle = (item.label.charCodeAt(0) / 255) * Math.PI * 2;
        item.vx = Math.cos(angle) * 92;
        item.vy = Math.sin(angle) * 92;
        return;
      }
      if (speed < 92) {
        const scale = 92 / speed;
        item.vx *= scale;
        item.vy *= scale;
      } else if (speed > 190) {
        const scale = 190 / speed;
        item.vx *= scale;
        item.vy *= scale;
      }
    };

    const movementLimits = (item) => {
      const strokeInset = width < 600 ? 1 : 0;
      const targetedLift = 2;
      const inkLimits = {
        minX: strokeInset - item.renderBounds.left,
        maxX: width - strokeInset - item.renderBounds.right,
        minY: strokeInset + targetedLift - item.renderBounds.top,
        maxY: height - strokeInset - item.renderBounds.bottom,
      };

      if (width >= 600) return inkLimits;

      return {
        minX: Math.max(inkLimits.minX, -1),
        maxX: Math.min(inkLimits.maxX, width - item.width + 1),
        minY: Math.max(inkLimits.minY, -1),
        maxY: Math.min(inkLimits.maxY, height - item.height + 1),
      };
    };

    const clampToBoundary = (item, bounce = true) => {
      const { minX, maxX, minY, maxY } = movementLimits(item);

      if (item.x <= minX) {
        item.x = minX;
        if (bounce) item.vx = Math.abs(item.vx);
      } else if (item.x >= maxX) {
        item.x = maxX;
        if (bounce) item.vx = -Math.abs(item.vx);
      }

      if (item.y <= minY) {
        item.y = minY;
        if (bounce) item.vy = Math.abs(item.vy);
      } else if (item.y >= maxY) {
        item.y = maxY;
        if (bounce) item.vy = -Math.abs(item.vy);
      }
    };

    const buildTextMask = (item) => {
      const text = item.node.querySelector(".floating-link-text");
      if (!text) return;

      const styles = getComputedStyle(text);
      const bleed = width < 600 ? 3 : 8;
      const configureContext = (context) => {
        context.fillStyle = "#000";
        context.textAlign = "start";
        context.textBaseline = "alphabetic";
        context.font = `${styles.fontStyle} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
        context.fontKerning = "normal";
        if ("letterSpacing" in context) context.letterSpacing = styles.letterSpacing;
      };

      const canvas = document.createElement("canvas");
      const measuringContext = canvas.getContext("2d", {
        willReadFrequently: true,
      });
      if (!measuringContext) return;
      configureContext(measuringContext);
      const metrics = measuringContext.measureText(item.label);
      const viewWidth =
        Math.max(
          1,
          metrics.width,
          metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight,
        ) +
        bleed * 2;
      const viewHeight = Math.max(
        1,
        metrics.actualBoundingBoxAscent +
          metrics.actualBoundingBoxDescent +
          bleed * 2,
      );
      const fontAscent =
        metrics.fontBoundingBoxAscent || metrics.actualBoundingBoxAscent;
      const fontDescent =
        metrics.fontBoundingBoxDescent || metrics.actualBoundingBoxDescent;

      canvas.width = Math.ceil(viewWidth * maskScale);
      canvas.height = Math.ceil(viewHeight * maskScale);
      item.node.style.width = `${viewWidth}px`;
      item.node.style.height = `${viewHeight}px`;
      text.style.left = `${bleed}px`;
      text.style.top = `${bleed - (fontAscent - metrics.actualBoundingBoxAscent)}px`;
      text.style.lineHeight = `${fontAscent + fontDescent}px`;
      item.width = viewWidth;
      item.height = viewHeight;

      const nodeBounds = item.node.getBoundingClientRect();
      const textBounds = text.getBoundingClientRect();
      item.renderBounds = {
        left: textBounds.left - nodeBounds.left,
        top: textBounds.top - nodeBounds.top,
        right: textBounds.right - nodeBounds.left,
        bottom: textBounds.bottom - nodeBounds.top,
      };

      const drawingContext = canvas.getContext("2d", {
        willReadFrequently: true,
      });
      if (!drawingContext) return;
      configureContext(drawingContext);
      drawingContext.scale(maskScale, maskScale);
      drawingContext.fillText(
        item.label,
        bleed + metrics.actualBoundingBoxLeft,
        bleed + metrics.actualBoundingBoxAscent,
      );
      const inkMask = readPixelMask(canvas);
      item.mask = dilatePixelMask(inkMask, 2);
      const bounds = opaquePixelBounds(inkMask);
      item.opaqueBounds = {
        left: (bounds.left / inkMask.width) * item.width,
        top: (bounds.top / inkMask.height) * item.height,
        right: (bounds.right / inkMask.width) * item.width,
        bottom: (bounds.bottom / inkMask.height) * item.height,
      };
    };

    const renderItemPosition = (item) => {
      item.node.style.left = `${Math.round(item.x)}px`;
      item.node.style.top = `${Math.round(item.y)}px`;
    };

    const conflictsAt = (item, x, y, others) => {
      const candidate = bodyForItem(item, x, y);
      return others.some((other) =>
        masksOverlap(candidate, bodyForItem(other)),
      );
    };

    const placeItem = (item, others, fallbackIndex) => {
      const { minX, maxX, minY, maxY } = movementLimits(item);

      for (let attempt = 0; attempt < 140; attempt += 1) {
        const x = minX + Math.random() * Math.max(0, maxX - minX);
        const y = minY + Math.random() * Math.max(0, maxY - minY);
        if (!conflictsAt(item, x, y, others)) {
          item.x = x;
          item.y = y;
          return;
        }
      }

      const fallback = fallbackPositions[fallbackIndex];
      item.x = minX + fallback.x * Math.max(0, maxX - minX);
      item.y = minY + fallback.y * Math.max(0, maxY - minY);
      clampToBoundary(item, false);
    };

    const randomizeItems = () => {
      const placed = [];
      items.forEach((item, index) => {
        placeItem(item, placed, index);
        const angle = Math.random() * Math.PI * 2;
        const speed = 104 + Math.random() * 70;
        item.vx = Math.cos(angle) * speed;
        item.vy = Math.sin(angle) * speed;
        placed.push(item);
      });
      initialized = true;
    };

    const placeItems = () => {
      const boundaryBounds = boundary.getBoundingClientRect();
      width = boundaryBounds.width;
      height = boundaryBounds.height;
      items.forEach(buildTextMask);

      if (!initialized) {
        randomizeItems();
      } else {
        items.forEach((item, index) => {
          clampToBoundary(item, false);
          const others = items.filter((other) => other !== item);
          if (conflictsAt(item, item.x, item.y, others)) {
            placeItem(item, others, index);
          }
        });
      }

      items.forEach((item) => {
        item.lastObservedX = item.x;
        item.lastObservedY = item.y;
        item.lastMovedAt = performance.now();
        item.node.style.visibility = "visible";
        renderItemPosition(item);
      });
      boundary.classList.add("has-floating-physics");
    };

    const inkRect = (item) => ({
      left: item.x + item.opaqueBounds.left,
      right: item.x + item.opaqueBounds.right,
      top: item.y + item.opaqueBounds.top,
      bottom: item.y + item.opaqueBounds.bottom,
    });

    const resolvePair = (a, b) => {
      const aRect = inkRect(a);
      const bRect = inkRect(b);
      const overlapX =
        Math.min(aRect.right, bRect.right) -
        Math.max(aRect.left, bRect.left);
      const overlapY =
        Math.min(aRect.bottom, bRect.bottom) -
        Math.max(aRect.top, bRect.top);
      if (overlapX <= 0 || overlapY <= 0) return false;

      const aCenterX = (aRect.left + aRect.right) / 2;
      const aCenterY = (aRect.top + aRect.bottom) / 2;
      const bCenterX = (bRect.left + bRect.right) / 2;
      const bCenterY = (bRect.top + bRect.bottom) / 2;
      const horizontal = overlapX < overlapY;
      const normalX = horizontal ? (aCenterX < bCenterX ? -1 : 1) : 0;
      const normalY = horizontal ? 0 : aCenterY < bCenterY ? -1 : 1;
      const correction = (horizontal ? overlapX : overlapY) + 0.75;
      const aShare = a.held && !b.held ? 0 : b.held && !a.held ? 1 : 0.5;
      const bShare = b.held && !a.held ? 0 : a.held && !b.held ? 1 : 0.5;

      a.x += normalX * correction * aShare;
      a.y += normalY * correction * aShare;
      b.x -= normalX * correction * bShare;
      b.y -= normalY * correction * bShare;
      clampToBoundary(a, false);
      clampToBoundary(b, false);

      const aNormalSpeed = a.vx * normalX + a.vy * normalY;
      const bNormalSpeed = b.vx * normalX + b.vy * normalY;
      const closingSpeed = aNormalSpeed - bNormalSpeed;
      if (a.held && !b.held) {
        const speed = Math.max(92, Math.abs(bNormalSpeed));
        b.vx += -normalX * speed - b.vx * Math.abs(normalX);
        b.vy += -normalY * speed - b.vy * Math.abs(normalY);
      } else if (b.held && !a.held) {
        const speed = Math.max(92, Math.abs(aNormalSpeed));
        a.vx += normalX * speed - a.vx * Math.abs(normalX);
        a.vy += normalY * speed - a.vy * Math.abs(normalY);
      } else if (!a.held && !b.held && closingSpeed < 0) {
        a.vx += (bNormalSpeed - aNormalSpeed) * normalX;
        a.vy += (bNormalSpeed - aNormalSpeed) * normalY;
        b.vx += (aNormalSpeed - bNormalSpeed) * normalX;
        b.vy += (aNormalSpeed - bNormalSpeed) * normalY;
      }

      return true;
    };

    const resolveCollisions = () => {
      for (let pass = 0; pass < 3; pass += 1) {
        let collisionFound = false;
        for (let first = 0; first < items.length; first += 1) {
          for (let second = first + 1; second < items.length; second += 1) {
            collisionFound =
              resolvePair(items[first], items[second]) || collisionFound;
          }
        }
        if (!collisionFound) break;
      }
    };

    const physicsStep = (elapsed) => {
      items.forEach((item) => {
        if (item.held) return;

        if (pointer.active) {
          const deltaX = item.x + item.width / 2 - pointer.x;
          const deltaY = item.y + item.height / 2 - pointer.y;
          const distance = Math.hypot(deltaX, deltaY);
          const radius = width < 600 ? 90 : 130;
          if (distance > 0 && distance < radius) {
            const force = (1 - distance / radius) * 160;
            item.vx += (deltaX / distance) * force * elapsed;
            item.vy += (deltaY / distance) * force * elapsed;
          }
        }
      });

      items.forEach((item) => {
        if (item.held) return;
        item.x += item.vx * elapsed;
        item.y += item.vy * elapsed;
        clampToBoundary(item);
      });

      resolveCollisions();
      items.forEach((item) => {
        if (!item.held) keepMoving(item);
      });
    };

    const animate = (time) => {
      frame = 0;
      if (disposed || document.hidden) return;
      const elapsed = previousTime
        ? Math.min((time - previousTime) / 1000, 0.035)
        : 0;
      previousTime = time;
      lastFrameAt = time;
      const fastest = Math.max(
        ...items.map((item) => Math.hypot(item.vx, item.vy)),
      );
      const substeps = Math.max(
        1,
        Math.min(8, Math.ceil((fastest * elapsed) / 1.5)),
      );
      for (let step = 0; step < substeps; step += 1) {
        physicsStep(elapsed / substeps);
      }

      items.forEach((item, index) => {
        if (item.held) {
          item.lastObservedX = item.x;
          item.lastObservedY = item.y;
          item.lastMovedAt = time;
          return;
        }

        const distanceMoved = Math.hypot(
          item.x - item.lastObservedX,
          item.y - item.lastObservedY,
        );
        if (distanceMoved >= 0.35) {
          item.lastObservedX = item.x;
          item.lastObservedY = item.y;
          item.lastMovedAt = time;
          return;
        }

        if (time - item.lastMovedAt < 1200) return;

        const others = items.filter((other) => other !== item);
        if (conflictsAt(item, item.x, item.y, others)) {
          placeItem(item, others, index);
        }
        const recoveryAngle =
          ((index + 1) * 1.71 + time / 997) % (Math.PI * 2);
        item.vx = Math.cos(recoveryAngle) * 138;
        item.vy = Math.sin(recoveryAngle) * 138;
        clampToBoundary(item, false);
        item.lastObservedX = item.x;
        item.lastObservedY = item.y;
        item.lastMovedAt = time;
      });

      items.forEach(renderItemPosition);
      frame = requestAnimationFrame(animate);
    };

    const startAnimation = () => {
      if (
        disposed ||
        frame ||
        document.hidden ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        return;
      }
      previousTime = 0;
      lastFrameAt = performance.now();
      frame = requestAnimationFrame(animate);
    };

    const pointHitsItem = (item, x, y) => {
      const padding = width < 600 ? 6 : 5;
      const left = item.x + item.opaqueBounds.left - padding;
      const top = item.y + item.opaqueBounds.top - padding;
      const right = item.x + item.opaqueBounds.right + padding;
      const bottom = item.y + item.opaqueBounds.bottom + padding;
      return x >= left && x < right && y >= top && y < bottom;
    };

    const itemAtPointer = (x, y) =>
      items.find((item) => pointHitsItem(item, x, y)) || null;

    const updatePointerHold = (hoveredItem) => {
      items.forEach((item) => {
        item.pointerHeld = item === hoveredItem || item === pressedItem;
        item.held = item.focused || item.pointerHeld;
        item.node.classList.toggle(
          "is-targeted",
          item === hoveredItem || item === pressedItem,
        );
        item.node.classList.toggle("is-pressed", item === pressedItem);
      });
      boundary.style.cursor = hoveredItem ? "pointer" : "";
    };

    const pointerPosition = (event) => {
      const bounds = boundary.getBoundingClientRect();
      return {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      };
    };

    const onPointerMove = (event) => {
      const position = pointerPosition(event);
      pointer = { ...position, active: true };
      updatePointerHold(itemAtPointer(position.x, position.y));
    };

    const onPointerLeave = () => {
      pointer.active = false;
      updatePointerHold(null);
    };

    const onPointerDown = (event) => {
      if (event.button !== 0) return;
      const position = pointerPosition(event);
      pressedItem = itemAtPointer(position.x, position.y);
      updatePointerHold(pressedItem);
      if (pressedItem) event.preventDefault();
    };

    const onPointerUp = (event) => {
      const position = pointerPosition(event);
      const releasedOver = itemAtPointer(position.x, position.y);
      pressedItem = null;
      pointer.active = event.pointerType === "mouse";
      updatePointerHold(pointer.active ? releasedOver : null);
    };

    const onPointerCancel = () => {
      pressedItem = null;
      pointer.active = false;
      updatePointerHold(null);
    };

    const onClick = (event) => {
      if (
        event.detail === 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const position = pointerPosition(event);
      const target = itemAtPointer(position.x, position.y);
      if (!target) return;
      event.preventDefault();
      window.location.assign(target.node.href);
    };

    const onVisibilityChange = () => {
      previousTime = 0;
      if (document.hidden) {
        cancelAnimationFrame(frame);
        frame = 0;
        onPointerCancel();
        return;
      }
      startAnimation();
    };

    const onPageShow = (event) => {
      if (!ready) return;
      if (event.persisted) {
        initialized = false;
        previousTime = 0;
        placeItems();
      }
      startAnimation();
    };

    const focusListeners = items.map((item) => {
      const hold = () => {
        item.focused = true;
        item.held = true;
      };
      const release = () => {
        item.focused = false;
        item.held = item.pointerHeld;
      };
      item.node.addEventListener("focus", hold);
      item.node.addEventListener("blur", release);
      return { node: item.node, hold, release };
    });

    const resizeObserver = new ResizeObserver(() => {
      if (ready) placeItems();
    });
    resizeObserver.observe(boundary);
    boundary.addEventListener("pointermove", onPointerMove);
    boundary.addEventListener("pointerleave", onPointerLeave);
    boundary.addEventListener("pointerdown", onPointerDown);
    boundary.addEventListener("pointercancel", onPointerCancel);
    boundary.addEventListener("click", onClick);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("blur", onPointerCancel);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);

    const cleanup = () => {
      if (disposed) return;
      disposed = true;
      boundary.classList.remove("has-floating-physics");
      cancelAnimationFrame(frame);
      window.clearInterval(animationWatchdog);
      resizeObserver.disconnect();
      boundary.removeEventListener("pointermove", onPointerMove);
      boundary.removeEventListener("pointerleave", onPointerLeave);
      boundary.removeEventListener("pointerdown", onPointerDown);
      boundary.removeEventListener("pointercancel", onPointerCancel);
      boundary.removeEventListener("click", onClick);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("blur", onPointerCancel);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
      focusListeners.forEach(({ node, hold, release }) => {
        node.removeEventListener("focus", hold);
        node.removeEventListener("blur", release);
      });
      activeHubs.delete(boundary);
    };
    activeHubs.set(boundary, cleanup);

    const initialize = async () => {
      await document.fonts.ready;
      if (disposed) return;
      ready = true;
      placeItems();
      startAnimation();
      animationWatchdog = window.setInterval(() => {
        if (
          ready &&
          !document.hidden &&
          !window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
          (!frame || performance.now() - lastFrameAt > 1500)
        ) {
          cancelAnimationFrame(frame);
          frame = 0;
          startAnimation();
        }
      }, 1000);
    };
    initialize();
  }

  function initializeWithin(root = document) {
    if (root.matches?.("[data-frml-floating-hub]")) initializeHub(root);
    root
      .querySelectorAll?.("[data-frml-floating-hub]")
      .forEach((hub) => initializeHub(hub));
  }

  function cleanupWithin(root) {
    const hubs = [];
    if (root.matches?.("[data-frml-floating-hub]")) hubs.push(root);
    root
      .querySelectorAll?.("[data-frml-floating-hub]")
      .forEach((hub) => hubs.push(hub));
    hubs.forEach((hub) => activeHubs.get(hub)?.());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeWithin());
  } else {
    initializeWithin();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeWithin(event.target);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    cleanupWithin(event.target);
  });
})();
